import React from 'react'
import { Box, Button, CircularProgress, Typography } from '@mui/material'
import { copyTree, getWorkflowStatus, treeManagementUrl } from './TreeActions'
import { directTreeAccessUrl } from 'infrastructure/api'
import { useGetTreeById } from 'shared/hooks/trees/hooks'
import { useWorkflowStatus } from 'shared/hooks/useWorkflowStatus'
import { openTab } from '../Helpers'

export function CopyAndShare({
  treeId,
  versionId,
}: {
  treeId: string
  versionId: string
}) {
  const { tree } = useGetTreeById({ treeId, versionId })
  const [newLink, setNewLink] = React.useState<string>()
  const [doingIt, setDoingIt] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)
  const { workflowStatus, lastChecked, startPolling, reset } =
    useWorkflowStatus(getWorkflowStatus)

  async function makeCopyAndShare() {
    if (!tree) return
    setDoingIt(true)
    setError(null)
    setNewLink(undefined)
    reset()
    try {
      const {
        treeId: newTreeId,
        versionId: newVersionId,
        workflowId,
      } = await copyTree({
        treeId: tree.tree_id,
        versionId: tree.version,
      })
      startPolling(workflowId, {
        onCompleted: () => {
          openTab(
            treeManagementUrl({
              treeId: newTreeId,
              versionId: newVersionId,
            }),
          )
          setNewLink(
            directTreeAccessUrl({
              treeId: newTreeId,
              versionId: newVersionId,
            }),
          )
        },
      })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to copy tree')
    } finally {
      setDoingIt(false)
    }
  }

  if (!tree) return <Box>Loading Tree...</Box>
  return (
    <Box>
      <Box padding={'5px 0 10px 5px'}>
        <b>Title: {tree.title}</b>
      </Box>
      <Box>
        <Button
          onClick={makeCopyAndShare}
          disabled={doingIt || workflowStatus === 'running'}
          startIcon={
            doingIt || workflowStatus === 'running' ? (
              <CircularProgress size={24} aria-label="Copy in progress" />
            ) : undefined
          }
        >
          {doingIt ? 'Dispatching copy...' : 'Make Copy and Share'}
        </Button>
      </Box>
      {error && (
        <Typography variant="body2" color="error" mt={1}>
          {error}
        </Typography>
      )}
      <Box aria-live="polite">
        {workflowStatus === 'running' && (
          <Box mt={1} display="flex" alignItems="center" gap={1}>
            <CircularProgress size={16} aria-label="Checking copy status" />
            <Typography variant="body2" color="text.secondary">
              Copy in progress...
              {lastChecked &&
                ` (last checked: ${lastChecked.toLocaleTimeString()})`}
            </Typography>
          </Box>
        )}
        {workflowStatus === 'completed' && (
          <Box mt={1}>
            <Typography variant="body2" color="success.main">
              Copy completed successfully
            </Typography>
            {newLink && (
              <Box mt={0.5}>
                <Typography variant="body2" color="text.secondary">
                  New Tree:
                </Typography>
                <a href={newLink} target="_blank" rel="noopener noreferrer">
                  {newLink}
                </a>
              </Box>
            )}
          </Box>
        )}
        {workflowStatus === 'failed' && (
          <Typography variant="body2" color="error" mt={1}>
            Copy workflow failed
          </Typography>
        )}
      </Box>
    </Box>
  )
}
